import React from 'react'
import { Stack, Select, MenuItem, FormControl, InputLabel, SelectProps } from '@mui/material'
import { useFormContext, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IssueEntity } from 'types/IssueEntity'
import { IssueType } from '@finance-lab/utils/types'

const validIssueTypes: IssueType[] = [
    'ABB Directed issue', // Accelerated bookbuilding (variant av Riktad emission)
    'Directed issue', // Riktad emission
    'Guarantor fee issue', // Garantiersättning
    'IPO', // IPO även på svenska, dvs börsintroduktion
    'Non-cash issue', // Apportemission
    // 'Other', // legacy issue type, keeping for possible inclusion in the future
    'Rights issue', // Företrädesemission
]

/**
 * This is a subset of validIssueTypes that are allowed to be changed by an admin in between each other.
 */
const validLimitedIssueTypes: IssueType[] = ['ABB Directed issue', 'Directed issue', 'Non-cash issue']

const getValidIssueTypes = () => {
    return validIssueTypes
}

const isValidIssueType = (issueType: string): issueType is IssueType => validIssueTypes.includes(issueType as IssueType)

const getValidLimitedIssueTypes = () => {
    return validLimitedIssueTypes
}

const isValidLimitedIssueType = (issueType: IssueType) => validLimitedIssueTypes.includes(issueType)

export type PlainIssueTypeSelectProps = {
    name?: string
    label?: string
    labelI18n?: string
    limited: boolean
    excludedIssueTypes?: IssueType[]
} & SelectProps

/**
 * A select component for choosing an issue type.
 */
function PlainIssueTypeSelect({
    name,
    label,
    labelI18n,
    limited,
    excludedIssueTypes = [],
    ...props
}: PlainIssueTypeSelectProps) {
    const { t } = useTranslation()
    name = name || 'type'
    label = label || (labelI18n ? t(labelI18n) : 'Emissionstyp')
    const issueTypes = limited ? validLimitedIssueTypes : validIssueTypes

    const filteredIssueTypes =
        excludedIssueTypes.length === 0
            ? issueTypes
            : issueTypes.filter((issueType) => !excludedIssueTypes.includes(issueType))
    return (
        <Stack spacing={1} direction='row'>
            <Select name={name} label={label} {...props}>
                <MenuItem value='' disabled>
                    Välj emissionstyp
                </MenuItem>
                {filteredIssueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                        <IssueTypeText issueType={type} />
                    </MenuItem>
                ))}
            </Select>
        </Stack>
    )
}

/**
 * A select component for choosing an issue type.
 * This component is intended to be used in a form by passing the formMethods prop.
 */
function FormIssueTypeSelect({
    name,
    label,
    labelI18n,
    formMethods,
    limited,
    ...props
}: PlainIssueTypeSelectProps & { formMethods: UseFormReturn }) {
    const { t } = useTranslation()
    name = name || 'type'
    label = label || (labelI18n ? t(labelI18n) : 'Emissionstyp')
    const defaultIssueType = '' // 'Rights issue'
    const issueTypes = limited ? validLimitedIssueTypes : validIssueTypes
    const { fullWidth } = props
    return (
        <Stack spacing={1} direction='row'>
            <FormControl fullWidth={fullWidth}>
                <InputLabel id='issue-type-select-label'>{label}</InputLabel>
                <Select
                    labelId='issue-type-select-label'
                    {...formMethods.register(name)}
                    label={label}
                    defaultValue={formMethods.getValues(name) || defaultIssueType}
                    {...props}
                >
                    <MenuItem value='' disabled>
                        Välj emissionstyp
                    </MenuItem>
                    {issueTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                            <IssueTypeText issueType={type} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    )
}

type IssueTypeSelectProps = PlainIssueTypeSelectProps & { formMethods?: UseFormReturn }

/**
 * A select component for choosing an issue type. It expects either a formMethods prop.
 * If formMethods is passed, the FormIssueTypeSelect component is used.
 * If formMethods is not passed, the PlainIssueTypeSelect component is used.
 */
function IssueTypeSelect({ formMethods, ...props }: IssueTypeSelectProps) {
    if (formMethods) {
        return <FormIssueTypeSelect formMethods={formMethods} {...props} />
    } else {
        return <PlainIssueTypeSelect {...props} />
    }
}

/**
 * A select component for choosing an issue type.
 * This component will use the formMethods from the closest FormProvider.
 */
function RegisteredIssueTypeSelect({ ...props }: PlainIssueTypeSelectProps) {
    const formMethods = useFormContext()
    return <IssueTypeSelect formMethods={formMethods} {...props} />
}

/**
 * Returns true if the field should be shown for the given issueType.
 */
function showField(issueType: IssueType, issueField: keyof IssueEntity) {
    if (issueType === 'Rights issue') {
        const excludedFields: Array<keyof IssueEntity> = [
            // IPO fields
            'ipoData',
            'ipoTradingDate',
            'ipoIntervalLow',
            'ipoIntervalHigh',
            'directListing',
            // Other fields
            'noncashConsideration',
            'earnout',
            'spac',
        ]
        if (excludedFields.includes(issueField)) {
            return false
        } else {
            return true
        }
    }
    if (issueType === 'Directed issue') {
        const includedFields: Array<keyof IssueEntity> = [
            'shareId',
            'generalMeetingDate',
            'offeredUnits',
            'unitSubPrice',
            'internalComment',
            'externalComment',
            'unitSubTotal',
            'cancelled',
            'secondaryOffering',
            'secondarySub',
            'advisorFields',
            'commentsFields',
            'cancelled',
            'investorsFields',
            'linkedIssues',
            'abbTarget',
        ]
        if (includedFields.includes(issueField)) {
            return true
        } else {
            return false
        }
    }
    if (issueType === 'ABB Directed issue') {
        const includedFields: Array<keyof IssueEntity> = [
            'shareId',
            'generalMeetingDate',
            'offeredUnits',
            'unitSubPrice',
            'internalComment',
            'externalComment',
            'unitSubTotal',
            'cancelled',
            'abbTarget',
            'secondaryOffering',
            'secondarySub',
            'advisorFields',
            'commentsFields',
            'cancelled',
            'investorsFields',
            'linkedIssues',
        ]
        if (includedFields.includes(issueField)) {
            return true
        } else {
            return false
        }
    }
    if (issueType === 'Non-cash issue') {
        const includedFields: Array<keyof IssueEntity> = [
            'shareId',
            'generalMeetingDate',
            'offeredUnits',
            'unitSubPrice',
            'internalComment',
            'externalComment',
            'unitSubTotal',
            'cancelled',
            'advisorFields',
            'commentsFields',
            'cancelled',
            'investorsFields',
            'noncashConsideration',
            'linkedIssues',
            'abbTarget',
            'earnout',
            'rto',
        ]
        if (includedFields.includes(issueField)) {
            return true
        } else {
            return false
        }
    }
    if (issueType === 'IPO') {
        const includedFields: Array<keyof IssueEntity> = [
            'ipoData',
            'shareId',
            'advisorFields',
            'subStartDate',
            'subEndDate',
            'publicationDate',
            'sharesInUnit',
            'unitSubPrice',
            'offeredUnits',
            'secondaryOffering',
            'noOverAllotment',
            'ipoTradingDate',
            'ipoIntervalLow',
            'ipoIntervalHigh',
            'overAllotmentAllocation',
            'overAllotmentSizeSecondary',
            'overAllotmentAllocationSecondary',
            'commentsFields',
            'unitSubTotal',
            'unitSubGuarantor',
            'unitPctSubGuarantor',
            'secondarySub',
            'investorsFields',
            'feeStructureFields',
            'linkedIssues',
            'spac',
        ]
        if (includedFields.includes(issueField)) {
            return true
        } else {
            return false
        }
    }
    if (issueType === 'Guarantor fee issue') {
        const includedFields: Array<keyof IssueEntity> = ['linkedIssues', 'shareId', 'unitSubTotal']
        if (includedFields.includes(issueField)) {
            return true
        } else {
            return false
        }
    }
    return true // default to show the field
}

/**
 * If the field should be shown for the given issueType, the children are returned.
 * If the issueType or issueField is missing, null is returned.
 * If the issueType is invalid, null is returned.
 * If the field should not be shown, null is returned.
 */
function ShouldShow({
    issueType,
    issueField,
    children,
}: {
    issueType: IssueType
    issueField: keyof IssueEntity
    children: React.ReactNode
}) {
    if (!issueType || !issueField) {
        console.log(`Missing ${issueType ? 'issueField' : 'issueType'} in ShouldShow`)
        return null
    }
    if (!isValidIssueType(issueType)) {
        console.log(`Invalid issueType: ${issueType}`)
        return null
    }
    if (!showField(issueType, issueField)) {
        return null
    }
    return children
}

/**
 * If any of the fields listed in issueFields should be shown for the given issueType,
 * true is returned. issueFields can be a comma separated string or an array.
 */
function showAnyField(issueType: IssueType, issueFields: Array<keyof IssueEntity> | string) {
    if (!issueType || !issueFields) {
        console.log(`Missing ${issueType ? 'issueFields' : 'issueType'} in shouldShowAny`)
        return false
    }
    if (!isValidIssueType(issueType)) {
        console.log(`Invalid issueType: ${issueType}`)
        return false
    }
    if (!Array.isArray(issueFields)) {
        issueFields = issueFields.split(',').map((field) => field.trim() as keyof IssueEntity)
    }
    for (const field of issueFields) {
        if (showField(issueType, field)) {
            return true
        }
    }
    return false
}

/**
 * If any of the fields listed in issueFields should be shown for the given issueType,
 * the children are returned.
 */
function ShouldShowAny({
    issueType,
    issueFields,
    children,
}: {
    issueType: IssueType
    issueFields: Array<keyof IssueEntity> | string
    children: React.ReactNode
}) {
    if (!showAnyField(issueType, issueFields)) {
        return null
    }
    return children
}

function IssueTypeText({ issueType }: { issueType: IssueType }) {
    const { t } = useTranslation()
    return <>{issueType && t(`entities.issueType.values.${issueType}`)}</>
}

export {
    ShouldShow,
    ShouldShowAny,
    RegisteredIssueTypeSelect,
    IssueTypeSelect,
    getValidIssueTypes,
    isValidIssueType,
    getValidLimitedIssueTypes,
    isValidLimitedIssueType,
    IssueTypeText,
    showField,
}
